import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useConfig } from '@/context/ConfigContext';
import { 
  Users, 
  LayoutDashboard, 
  ListChecks, 
  ClipboardCheck,
  Bell,
  Lock,
  LogOut,
  Menu,
  X,
  Shield,
  Package,
  PackageOpen,
  DollarSign,
  ChevronDown,
  ChevronRight,
  Home,
  Briefcase,
  BarChart3,
  Settings,
  History,
  AlertTriangle,
  HelpCircle,
  Info,
  FileWarning,
  PackageX,
  Cake,
  UserCheck,
  Link2,
  Inbox,
  ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const Layout = ({ children }) => {
  const authContext = useAuth();
  const config = useConfig();
  const {
    user, logout, role, token, loading,
    canManageUsers = false,
    canViewHistory = false,
    canManageFinance = false,
    canManageMaterials = false,
    canSubtractMaterials = false,
    canManageCategories = false,
    canManagePlayers = false,
    canViewDashboard = false,
    canViewMap = false,
    canManageContracts = false,
    canManageTracking = false,
    isSuperAdmin = false,
    isAdmin = false,
    isLegales = false,
    isScout = false,
  } = authContext || {};
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [alerts, setAlerts] = useState({ 
    certificates: { expired: [], expiring: [] }, 
    lowStock: [],
    signings: { urgent: [], warning: [], upcoming: [] }
  });
  const [alertsOpen, setAlertsOpen] = useState(false);
  
  // Si está cargando, mostrar pantalla de carga
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
          <p className="mt-4 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }
  
  // Estado de secciones expandidas - persistido en localStorage
  const [expandedSections, setExpandedSections] = useState(() => {
    const saved = localStorage.getItem('sidebar_expanded_sections');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return { principal: true, herramientas: false, configuracion: false };
      }
    }
    return { principal: true, herramientas: false, configuracion: false };
  });

  // Guardar estado de secciones en localStorage
  useEffect(() => {
    localStorage.setItem('sidebar_expanded_sections', JSON.stringify(expandedSections));
  }, [expandedSections]);

  // Función para obtener alertas - definida como referencia estable
  const fetchAlerts = React.useCallback(async () => {
    if (!token) return;
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Fetch players for certificate alerts
      const playersRes = await fetch(`${(window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "")}/api/players?limit=1000`, { headers });
      const playersData = await playersRes.json();
      const players = playersData.data || [];
      
      // Check certificate alerts - using health_certificate_expiry and fitness_certificate_expiry
      const today = new Date();
      const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
      
      const expired = players.filter(p => {
        const healthExp = p.health_certificate_expiry ? new Date(p.health_certificate_expiry) : null;
        const fitnessExp = p.fitness_certificate_expiry ? new Date(p.fitness_certificate_expiry) : null;
        return (healthExp && healthExp < today) || (fitnessExp && fitnessExp < today);
      });
      
      const expiring = players.filter(p => {
        const healthExp = p.health_certificate_expiry ? new Date(p.health_certificate_expiry) : null;
        const fitnessExp = p.fitness_certificate_expiry ? new Date(p.fitness_certificate_expiry) : null;
        const healthExpiring = healthExp && healthExp >= today && healthExp <= thirtyDaysFromNow;
        const fitnessExpiring = fitnessExp && fitnessExp >= today && fitnessExp <= thirtyDaysFromNow;
        // Exclude already expired ones
        const healthExpired = healthExp && healthExp < today;
        const fitnessExpired = fitnessExp && fitnessExp < today;
        return (healthExpiring && !healthExpired) || (fitnessExpiring && !fitnessExpired);
      });

      // Fetch material alerts
      const materialsRes = await fetch(`${(window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "")}/api/materials/summary`, { headers });
      const materialsData = await materialsRes.json();
      const lowStock = materialsData?.low_stock_items || [];

      // Fetch signing alerts (players approaching 13th birthday)
      let signingsAlerts = { urgent: [], warning: [], upcoming: [] };
      try {
        const signingsRes = await fetch(`${(window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "")}/api/signings/alerts`, { headers });
        if (signingsRes.ok) {
          const signingsData = await signingsRes.json();
          const allSignings = signingsData.alerts || [];
          signingsAlerts = {
            urgent: allSignings.filter(s => s.alert_level === 'urgent'),
            warning: allSignings.filter(s => s.alert_level === 'warning'),
            upcoming: allSignings.filter(s => s.alert_level === 'upcoming')
          };
        }
      } catch (e) {
        console.error('Error fetching signing alerts:', e);
      }

      setAlerts({
        certificates: { expired, expiring },
        lowStock,
        signings: signingsAlerts
      });
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  }, [token]);

  // Fetch alerts on mount and periodically
  useEffect(() => {
    // Solo usuarios que pueden ver dashboard o materiales ven alertas
    if (token && (canViewDashboard || canManageMaterials)) {
      fetchAlerts();
      // Refresh alerts every 60 seconds
      const interval = setInterval(fetchAlerts, 60000);
      return () => clearInterval(interval);
    }
  }, [token, canViewDashboard, canManageMaterials, fetchAlerts]);
  
  // Also refresh alerts when navigating (location changes)
  useEffect(() => {
    if (token && (canViewDashboard || canManageMaterials)) {
      fetchAlerts();
    }
  }, [location.pathname, token, canViewDashboard, canManageMaterials, fetchAlerts]);

  const totalSigningAlerts = alerts.signings.urgent.length + alerts.signings.warning.length;
  const totalAlerts = alerts.certificates.expired.length + alerts.certificates.expiring.length + alerts.lowStock.length + totalSigningAlerts;

  const getMenuSections = () => {
    // Menú basado en permisos granulares (4 roles: superadmin, admin, legales, scout)
    const allSections = [
      {
        id: 'principal',
        label: 'Principal',
        icon: Home,
        showAlways: true,
        items: [
          { path: '/', label: 'Dashboard', icon: LayoutDashboard, show: canViewDashboard },
          { path: '/seguimiento', label: 'Seguimiento', icon: ClipboardCheck, show: canManageTracking },
          { path: '/players', label: 'Jugadores', icon: Users, show: canManagePlayers || isScout },
          { path: '/investments', label: 'Inversiones', icon: DollarSign, show: canManageMaterials || canSubtractMaterials },
          { path: '/finance', label: 'Finanzas', icon: DollarSign, show: canManageFinance },
        ]
      },
      {
        id: 'herramientas',
        label: 'Herramientas',
        icon: Briefcase,
        showAlways: true,
        items: [
          { path: '/history', label: 'Historial', icon: History, show: canViewHistory },
          { path: '/analytics', label: 'Reportes', icon: BarChart3, show: isSuperAdmin || isAdmin },
        ]
      },
      {
        id: 'configuracion',
        label: 'Configuración',
        icon: Settings,
        showAlways: false,
        showForRoles: ['superadmin', 'admin'],
        items: [
          { path: '/finance?tab=categories', label: 'Categorías Finanzas', icon: ListChecks, show: isSuperAdmin },
          { path: '/users', label: 'Usuarios', icon: Shield, show: canManageUsers },
          { path: '/scout-codes', label: 'Códigos Formulario', icon: Link2, show: isSuperAdmin || isAdmin },
          { path: '/formularios-recibidos', label: 'Formularios Recibidos', icon: Inbox, show: isSuperAdmin || isAdmin || isScout },
          { path: '/formulario', label: 'Ver Formulario Público', icon: ExternalLink, show: true, external: true },
        ]
      }
    ];

    return allSections.filter(section => {
      if (section.showAlways) return true;
      if (section.showForRoles && section.showForRoles.includes(role)) return true;
      return false;
    }).map(section => ({
      ...section,
      items: section.items.filter(item => item.show)
    })).filter(section => section.items.length > 0);
  };

  // Recalcular el menú cuando cambian los permisos
  const menuSections = React.useMemo(() => getMenuSections(), [
    role, canManageUsers, canViewHistory, canManageFinance, canManageMaterials,
    canManageCategories, canManagePlayers, canViewDashboard, canViewMap,
    canSubtractMaterials, canManageContracts, canManageTracking,
    isScout, isSuperAdmin, isAdmin, isLegales
  ]);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Inicializar secciones expandidas - NO se auto-expande al navegar
  // El usuario controla manualmente qué secciones están abiertas

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => {
    // Para paths con query parameters (como /materials?tab=categories)
    // debemos comparar la URL completa
    const currentFullPath = location.pathname + location.search;
    
    // Si el path del menú tiene query params, comparar exactamente
    if (path.includes('?')) {
      return currentFullPath === path;
    }
    
    // Si el path del menú NO tiene query params, solo es activo si la URL actual
    // tampoco tiene query params O tiene query params diferentes a "tab"
    const hasTabQuery = location.search.includes('tab=');
    if (hasTabQuery) {
      // Si la URL actual tiene ?tab=..., no marcar el path base como activo
      return false;
    }
    
    return location.pathname === path;
  };

  const renderMenuSection = (section, isMobile = false) => {
    const isExpanded = expandedSections[section.id];
    const SectionIcon = section.icon;
    const hasActiveItem = section.items.some(item => isActive(item.path));

    return (
      <div key={section.id} className="mb-1">
        <button
          onClick={() => toggleSection(section.id)}
          className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg text-xs font-semibold tracking-wider uppercase transition-all duration-200 ${
            hasActiveItem 
              ? 'text-brand-700 bg-brand-50/70' 
              : 'text-brand-600/70 hover:bg-gray-50 hover:text-brand-700'
          }`}
        >
          <div className="flex items-center">
            <SectionIcon className="w-4 h-4 mr-3" />
            {section.label}
          </div>
          <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? 'rotate-0' : '-rotate-90'}`} />
        </button>
        
        <div 
          className={`ml-4 space-y-0.5 overflow-hidden transition-all duration-300 ease-in-out ${
            isExpanded ? 'max-h-96 opacity-100 mt-1' : 'max-h-0 opacity-0'
          }`}
        >
          {section.items.map((item) => {
            const Icon = item.icon;
            if (item.external) {
              return (
                <a
                  key={item.path}
                  href={item.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => isMobile && setSidebarOpen(false)}
                  className="flex items-center px-4 py-2 rounded-lg text-sm transition-all duration-200 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                >
                  <Icon className="w-4 h-4 mr-3" />
                  {item.label}
                </a>
              );
            }
            return (
              <Link
                key={item.path}
                to={item.disabled ? '#' : item.path}
                onClick={(e) => {
                  if (item.disabled) {
                    e.preventDefault();
                  } else if (isMobile) {
                    setSidebarOpen(false);
                  }
                }}
                className={`flex items-center px-4 py-2 rounded-lg text-sm transition-all duration-200 ${
                  item.disabled
                    ? 'text-gray-400 cursor-not-allowed'
                    : isActive(item.path)
                    ? 'bg-brand-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <Icon className="w-4 h-4 mr-3" />
                {item.label}
              </Link>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar Desktop */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 bg-white border-r border-gray-200">
        <div className="p-5 border-b border-gray-200">
          <Link to="/" className="flex justify-center">
            <img 
              src={config.AGENCY_LOGO_DARK_URL} 
              alt={config.AGENCY_NAME} 
              className="h-7 w-auto"
            />
          </Link>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {menuSections.map(section => renderMenuSection(section))}

        </nav>

        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center mb-3">
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-brand-600 text-white text-sm">
                {user?.username?.substring(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-900">{user?.username}</p>
              <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
            </div>
          </div>
          <div className="space-y-1 mb-2">
            <Link
              to="/about"
              className="flex items-center px-3 py-2 rounded-lg text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
            >
              <Info className="w-4 h-4 mr-2" />
              Acerca de
            </Link>
            <Link
              to="/manual"
              className="flex items-center px-3 py-2 rounded-lg text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              Manual de Uso
            </Link>
          </div>
          <Button
            onClick={handleLogout}
            variant="ghost"
            size="sm"
            className="w-full justify-start text-gray-600 hover:bg-gray-100 hover:text-gray-900"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="fixed inset-y-0 left-0 w-64 bg-white z-50 flex flex-col shadow-xl">
            <div className="p-5 border-b border-gray-200 flex items-center justify-between">
              <Link to="/" className="flex-1 flex justify-center">
                <img 
                  src={config.AGENCY_LOGO_DARK_URL} 
                  alt={config.AGENCY_NAME} 
                  className="h-7 w-auto"
                />
              </Link>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(false)}
                className="text-gray-500 hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
              {menuSections.map(section => renderMenuSection(section, true))}
            </nav>

            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center mb-3">
                <Avatar className="h-9 w-9">
                  <AvatarFallback className="bg-brand-600 text-white text-sm">
                    {user?.username?.substring(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-gray-900">{user?.username}</p>
                  <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
                </div>
              </div>
              <Button
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="w-full justify-start text-gray-600 hover:bg-gray-100 hover:text-gray-900"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Cerrar Sesión
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 px-4 lg:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-5 w-5" />
              </Button>
              
              {/* Notifications Bell */}
              {(isAdmin || role === 'user') && (
                <Popover open={alertsOpen} onOpenChange={setAlertsOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="icon" className="relative">
                      <Bell className="h-5 w-5 text-gray-600" />
                      {totalAlerts > 0 && (
                        <span className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-medium">
                          {totalAlerts > 99 ? '99+' : totalAlerts}
                        </span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0" align="start">
                    <div className="p-3 border-b border-gray-100 bg-gray-50">
                      <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-500" />
                        Alertas Pendientes
                      </h4>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {totalAlerts === 0 ? (
                        <div className="p-4 text-center text-gray-500 text-sm">
                          No hay alertas pendientes
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-100">
                          {alerts.certificates.expired.length > 0 && (
                            <div 
                              className="block p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                              onClick={() => {
                                setAlertsOpen(false);
                                // Force navigation even if on same route by adding timestamp
                                navigate(`/players?certificate_status=expired&_t=${Date.now()}`);
                              }}
                            >
                              <div className="flex items-start gap-3">
                                <div className="p-1.5 bg-red-100 rounded">
                                  <FileWarning className="h-4 w-4 text-red-600" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">Certificados Vencidos</p>
                                  <p className="text-xs text-red-600">{alerts.certificates.expired.length} jugador(es)</p>
                                </div>
                              </div>
                            </div>
                          )}
                          {alerts.certificates.expiring.length > 0 && (
                            <div 
                              className="block p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                              onClick={() => {
                                setAlertsOpen(false);
                                navigate(`/players?certificate_status=expiring&_t=${Date.now()}`);
                              }}
                            >
                              <div className="flex items-start gap-3">
                                <div className="p-1.5 bg-amber-100 rounded">
                                  <FileWarning className="h-4 w-4 text-amber-600" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">Certificados por Vencer</p>
                                  <p className="text-xs text-amber-600">{alerts.certificates.expiring.length} jugador(es) (próximos 30 días)</p>
                                </div>
                              </div>
                            </div>
                          )}
                          {alerts.lowStock.length > 0 && (
                            <div 
                              className="block p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                              onClick={() => {
                                setAlertsOpen(false);
                                navigate(`/investments?tab=inventory&filter=low_stock&_t=${Date.now()}`);
                              }}
                            >
                              <div className="flex items-start gap-3">
                                <div className="p-1.5 bg-orange-100 rounded">
                                  <PackageX className="h-4 w-4 text-orange-600" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">Stock Bajo</p>
                                  <p className="text-xs text-orange-600">{alerts.lowStock.length} material(es)</p>
                                </div>
                              </div>
                            </div>
                          )}
                          {/* Signing Alerts - Urgent (7 days or less) */}
                          {alerts.signings.urgent.length > 0 && (
                            <div 
                              className="block p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                              onClick={() => {
                                setAlertsOpen(false);
                                navigate(`/signings?filter=urgent&_t=${Date.now()}`);
                              }}
                            >
                              <div className="flex items-start gap-3">
                                <div className="p-1.5 bg-red-100 rounded">
                                  <Cake className="h-4 w-4 text-red-600" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">Fichajes Urgentes</p>
                                  <p className="text-xs text-red-600">{alerts.signings.urgent.length} jugador(es) cumplen 13 en menos de 7 días</p>
                                </div>
                              </div>
                            </div>
                          )}
                          {/* Signing Alerts - Warning (30 days or less) */}
                          {alerts.signings.warning.length > 0 && (
                            <div 
                              className="block p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                              onClick={() => {
                                setAlertsOpen(false);
                                navigate(`/signings?filter=warning&_t=${Date.now()}`);
                              }}
                            >
                              <div className="flex items-start gap-3">
                                <div className="p-1.5 bg-amber-100 rounded">
                                  <Cake className="h-4 w-4 text-amber-600" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">Fichajes Próximos</p>
                                  <p className="text-xs text-amber-600">{alerts.signings.warning.length} jugador(es) cumplen 13 en menos de 30 días</p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
            
            <div className="hidden lg:block">
              <p className="text-sm text-gray-500">Bienvenido, <span className="font-medium text-gray-900">{user?.username}</span></p>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-4 lg:p-6">
          {children}
        </main>

        {/* Footer with Version */}
        <footer className="bg-gray-50 border-t border-gray-200 px-4 py-2 text-center">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-brand-100 text-brand-800">
            v3.6
          </span>
        </footer>
      </div>
    </div>
  );
};

export default Layout;
